import {makeAutoObservable, observable, runInAction} from 'mobx';
import type {StudioVersion} from '../../../data/api/StudioApi';
import StudioRepository from '../../../data/repositories/StudioRepository';
import type {StudioResponse} from '../../../data/repositories/StudioRepository';
import {logoFileProblem, logoToPng} from '../components/logoImage';
import {normalizeHex} from '../components/sourceBrand';
import {sourceBrandText as text} from '../components/sourceBrandText';
import {errorMessage} from '../errors';
import type {BrandColorRole, JobView} from '../types';
import {MutationScope, scopedIdentity} from './MutationScope';
import {studioRequest} from './studioRequests';
import type StudioJobStore from './StudioJobStore';

/** What is being saved: a color role, the font, the logo, or its removal. */
export type BrandSaving =
  | BrandColorRole
  | 'font'
  | 'logo'
  | 'removeLogo'
  | null;

/** The colors typed or picked and not saved yet, by role. */
export type BrandDrafts = Partial<Record<BrandColorRole, string>>;

/** What a color change is checked against. */
export interface BrandColorChange {
  /** Applies the saved job before generation is unlocked. */
  onSaved?: (view: JobView) => void;
  /** The storeVersion the panel shows, which the change is made against. */
  storeVersion: StudioVersion;
  /** The role's color as the panel shows it saved, if it has one. */
  saved?: string;
}

/** What a font change is checked against. */
export interface BrandFontChange {
  /** Applies the saved job before generation is unlocked. */
  onSaved?: (view: JobView) => void;
  /** The storeVersion the panel shows, which the change is made against. */
  storeVersion: StudioVersion;
  /**
   * The font the panel shows chosen: a font's id (Montserrat's for a brand without one), or
   * "serif" for the document's matched serif.
   */
  shown: string;
}

/**
 * What `commit` or `updateFont` did: the saved job; 'unchanged' when the color or font was
 * already saved (a color's draft is dropped); or null when nothing was saved.
 */
export type BrandCommit = JobView | 'unchanged' | null;

/**
 * Changes the open job's video branding: its colors, its font and its logo. One change at a
 * time; each resolves to the saved job (whose storyboard now needs a new render), or null when
 * it failed (see `problem`), another change was running, or the panel closed meanwhile.
 *
 * Registered in storesProvider as `studioBrandStore`. The branding panel (SourceBrandSummary,
 * one per page) opens it for its job with `open(organizationId, jobId)` and closes it when it
 * goes away. Closing drops the drafts, the change being saved and the last problem, and a
 * change that finishes after its panel closed cannot change the next panel's drafts or
 * errors. Its saved job still updates the job store when the same job is open, before
 * generation unlocks. Pages pass `edit={{storeVersion, onSaved}}` to apply an active panel's
 * save and show its notice before generation unlocks.
 */
export default class BrandStore {
  saving: BrandSaving = null;
  /** Why the last change failed, written for authors. */
  problem: string | null = null;
  /**
   * The change `problem` is about, so the panel shows it beside that change: the one that
   * failed, or null for a color that is not a hex color.
   */
  failed: BrandSaving = null;
  drafts: BrandDrafts = {};
  /** The font being saved, shown chosen until the saved job arrives. */
  font: string | null = null;
  organizationId: string | undefined = undefined;
  jobId = '';

  private repository = new StudioRepository();
  private scope = new MutationScope();
  // Counts the panels opened, so their drafts and errors stay separate.
  private opened = 0;
  // A request outlives its panel, including a move between this job's pages.
  private pending: Record<string, number> = {};

  constructor(private readonly jobs: StudioJobStore) {
    makeAutoObservable<
      BrandStore,
      'repository' | 'scope' | 'opened' | 'jobs' | 'pending'
    >(this, {
      // Replaced on each change, and handed to the panel as a plain object.
      drafts: observable.ref,
      repository: false,
      scope: false,
      opened: false,
      jobs: false,
      pending: observable.ref,
    });
  }

  /**
   * The branding panel shows this job: changes are made to it. Starts afresh, and returns the
   * function that closes it (see `close`), for an effect's cleanup.
   */
  open(organizationId: string | undefined, jobId: string) {
    this.close();
    const opened = ++this.opened;
    this.organizationId = organizationId;
    this.jobId = jobId;
    this.scope.setIdentity(
      scopedIdentity(organizationId, `${jobId}#${opened}`),
    );
    this.scope.activate();
    return () => {
      if (this.opened === opened) this.close();
    };
  }

  /** The panel went away: its drafts and problem go; in-flight saves still block their job. */
  close() {
    this.scope.deactivate();
    this.saving = null;
    this.setProblem(null);
    this.drafts = {};
    this.font = null;
  }

  /** Only the open job's pending brand save blocks its generation or sharing. */
  isSavingFor(organizationId: string | undefined, jobId: string | undefined) {
    return !!this.pending[scopedIdentity(organizationId, String(jobId))];
  }

  /** Says why a change failed: `failed` names that change, if it was sent or checked. */
  setProblem(problem: string | null, failed: BrandSaving = null) {
    this.problem = problem;
    this.failed = problem === null ? null : failed;
  }

  /** Keeps what the author typed or picked for a role, until it is saved or reverted. */
  setDraft(role: BrandColorRole, value: string) {
    this.drafts = {...this.drafts, [role]: value};
  }

  /** Drops the role's draft and the message about it; a refused font's message stays. */
  revert(role: BrandColorRole) {
    this.clearDraft(role);
    if (this.failed !== 'font') this.setProblem(null);
  }

  /**
   * Saves `value` (a color picked on the picker), or else the role's draft. A color that is not
   * a hex color is a problem, and one already saved is not sent; see `BrandCommit`. Nothing is
   * saved without a color to save, while another change runs, or when the save fails.
   */
  async commit(
    role: BrandColorRole,
    change: BrandColorChange,
    value?: string,
  ): Promise<BrandCommit> {
    const draft = value ?? this.drafts[role];
    if (draft === undefined || this.saving || !this.isOpen()) return null;
    const hex = normalizeHex(draft);
    if (!hex) {
      this.setProblem(text.invalidHex);
      return null;
    }
    if (hex === change.saved) {
      this.clearDraft(role);
      return 'unchanged';
    }
    const view = await this.saveColor(
      role,
      hex,
      change.storeVersion,
      change.onSaved,
    );
    if (view) runInAction(() => this.clearDraft(role));
    return view;
  }

  saveColor(
    role: BrandColorRole,
    hex: string,
    storeVersion: StudioVersion,
    onSaved?: (view: JobView) => void,
  ) {
    return this.change(
      role,
      (org, jobId) =>
        this.repository.updateBrandColors(
          org,
          jobId,
          {[role]: hex},
          storeVersion,
        ),
      onSaved,
    );
  }

  /**
   * Saves the font with this id (one GET /options lists) for the next render. The font shown
   * chosen is not sent again; see `BrandCommit`.
   */
  async updateFont(
    fontFamily: string,
    change: BrandFontChange,
  ): Promise<BrandCommit> {
    const current = this.scope.begin();
    if (this.saving || !current()) return null;
    if (fontFamily === change.shown) return 'unchanged';
    this.font = fontFamily;
    const view = await this.change(
      'font',
      (org, jobId) =>
        this.repository.updateBrandFont(
          org,
          jobId,
          fontFamily,
          change.storeVersion,
        ),
      change.onSaved,
    );
    // Saved, the job shows it; failed, the saved font shows again. A closed panel's is gone.
    if (current())
      runInAction(() => {
        this.font = null;
      });
    return view;
  }

  /** Converts the file to the PNG Studio stores, then uploads it. */
  uploadLogo(
    file: File,
    storeVersion: StudioVersion,
    onSaved?: (view: JobView) => void,
  ) {
    if (this.saving || !this.isOpen()) return Promise.resolve(null);
    const issue = logoFileProblem(file);
    if (issue) {
      this.setProblem(issue, 'logo');
      return Promise.resolve(null);
    }
    return this.change(
      'logo',
      async (org, jobId) =>
        this.repository.uploadBrandLogo(
          org,
          jobId,
          await logoToPng(file),
          storeVersion,
        ),
      onSaved,
    );
  }

  /** The next render has no logo, even if the document had one. */
  removeLogo(storeVersion: StudioVersion, onSaved?: (view: JobView) => void) {
    return this.change(
      'removeLogo',
      (org, jobId) => this.repository.removeBrandLogo(org, jobId, storeVersion),
      onSaved,
    );
  }

  private clearDraft(role: BrandColorRole) {
    this.drafts = {...this.drafts, [role]: undefined};
  }

  /** Whether a panel has the store open now. */
  private isOpen() {
    return this.scope.begin()();
  }

  private async change(
    saving: Exclude<BrandSaving, null>,
    send: (
      organizationId: string,
      jobId: string,
    ) => Promise<StudioResponse<JobView>>,
    onSaved?: (view: JobView) => void,
  ): Promise<JobView | null> {
    const current = this.scope.begin();
    if (this.saving || !current()) return null;
    const {organizationId, jobId} = this;
    const identity = scopedIdentity(organizationId, jobId);
    runInAction(() => {
      this.pending = {
        ...this.pending,
        [identity]: (this.pending[identity] || 0) + 1,
      };
      this.saving = saving;
      this.setProblem(null);
    });
    try {
      const view = await studioRequest(organizationId, org => send(org, jobId));
      if (!current()) {
        // The author moved to another page of this job: apply the completed change there,
        // before allowing generation. A different job (or organization) ignores it.
        this.jobs.setView(view, identity);
        return null;
      }
      onSaved?.(view);
      return view;
    } catch (e) {
      if (current())
        runInAction(() => this.setProblem(errorMessage(e), saving));
      return null;
    } finally {
      runInAction(() => {
        const pending = {...this.pending};
        if (--pending[identity] === 0) delete pending[identity];
        this.pending = pending;
      });
      if (current())
        runInAction(() => {
          this.saving = null;
        });
    }
  }
}
